import { type FC, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import "chartjs-adapter-moment";
import {
  Chart as ChartJS,
  Filler,
  Legend as ChartLegend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip as ChartTooltip,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from "chart.js";
import { Line } from "react-chartjs-2";
import type { ChartTimeUnit } from "../model/types";

// Контроллер линии react-chartjs-2 регистрирует сам при создании `Line`,
// здесь только элементы, шкалы и плагины (как в shared/components/BarChart).
ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  ChartTooltip,
  ChartLegend,
  Filler
);

export interface MonitoringPoint {
  x: string;
  y: number | null;
}

export interface MonitoringSeries {
  label: string;
  data: MonitoringPoint[];
  /** Цвет линии; без него берётся из палитры темы по порядку серий. */
  color?: string;
}

/** Промежуток без видео: полоса поперёк графика. */
export interface MonitoringBand {
  start: string;
  end: string;
}

interface BandsPluginOptions {
  bands?: MonitoringBand[];
  color?: string;
}

// Полосы обрывов рисуются под линиями (beforeDatasetsDraw) по шкале x.
const outageBandsPlugin: Plugin<"line"> = {
  id: "outageBands",
  beforeDatasetsDraw(chart, _args, options: BandsPluginOptions) {
    const bands = options.bands ?? [];
    const { ctx, chartArea, scales } = chart;
    const x = scales.x;
    if (!bands.length || !x || !chartArea) return;
    ctx.save();
    ctx.fillStyle = options.color ?? "rgba(244, 67, 54, 0.15)";
    for (const band of bands) {
      const left = Math.max(
        x.getPixelForValue(new Date(band.start).getTime()),
        chartArea.left
      );
      const right = Math.min(
        x.getPixelForValue(new Date(band.end).getTime()),
        chartArea.right
      );
      if (right > left) {
        ctx.fillRect(left, chartArea.top, right - left, chartArea.height);
      }
    }
    ctx.restore();
  },
};

interface MonitoringLineChartProps {
  title: string;
  hint?: string;
  series: MonitoringSeries[];
  /** Шаг делений оси времени: минуты для MINUTE-гранулярности до 3 ч, иначе часы. */
  unit: ChartTimeUnit;
  yLabel?: string;
  /** Горизонтальная опорная линия (100 % для скорости загрузки). */
  referenceY?: number;
  loading?: boolean;
  /** Показать заглушку «нет данных». */
  empty?: boolean;
  emptyLabel: string;
  height?: number;
  /** Промежутки без видео (outages.periods), полосами на фоне. */
  bands?: MonitoringBand[];
  /** Контурная карточка: график внутри другой карточки (метрики плеера). */
  outlined?: boolean;
}

const DEFAULT_HEIGHT = 220;

export const MonitoringLineChart: FC<MonitoringLineChartProps> = ({
  title,
  hint,
  series,
  unit,
  yLabel,
  referenceY,
  loading = false,
  empty = false,
  emptyLabel,
  height = DEFAULT_HEIGHT,
  bands,
  outlined = false,
}) => {
  const theme = useTheme();

  const hasData = series.some((item) =>
    item.data.some((point) => point.y !== null)
  );

  const data = useMemo<ChartData<"line", MonitoringPoint[]>>(() => {
    const seriesColors = [
      theme.palette.primary.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.success.main,
    ];
    const datasets: ChartData<"line", MonitoringPoint[]>["datasets"] =
      series.map((item, index) => {
        const color = item.color ?? seriesColors[index % seriesColors.length];
        return {
          label: item.label,
          data: item.data,
          borderColor: color,
          backgroundColor: color,
          borderWidth: 2,
          pointRadius: 0,
          pointHitRadius: 8,
          tension: 0.2,
          spanGaps: false,
        };
      });

    const allPoints = series.flatMap((item) => item.data);
    const first = allPoints[0];
    const last = allPoints[allPoints.length - 1];
    if (referenceY !== undefined && first && last) {
      datasets.push({
        // Пустая подпись исключает опорную линию из легенды (см. filter ниже).
        label: "",
        data: [
          { x: first.x, y: referenceY },
          { x: last.x, y: referenceY },
        ],
        borderColor: theme.palette.text.disabled,
        borderWidth: 1,
        borderDash: [6, 4],
        pointRadius: 0,
        pointHitRadius: 0,
      });
    }

    return { datasets };
  }, [series, referenceY, theme]);

  const options = useMemo<ChartOptions<"line">>(
    () => ({
      animation: false,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        // Свой плагин не входит в типы плагинов Chart.js, отсюда приведение.
        ...({
          outageBands: {
            bands,
            color: alpha(theme.palette.error.main, 0.18),
          },
        } as object),
        legend: {
          display: series.length > 1,
          labels: { filter: (item) => Boolean(item.text) },
        },
        tooltip: {
          // Опорная линия добавляется последним датасетом, в тултип не идёт.
          filter: (item) => item.datasetIndex < series.length,
        },
      },
      scales: {
        x: { type: "time", time: { unit } },
        y: {
          beginAtZero: true,
          title: { display: Boolean(yLabel), text: yLabel },
        },
      },
    }),
    [series.length, unit, yLabel, bands, theme]
  );

  let body;
  if (loading && !hasData) {
    body = <Skeleton variant="rounded" height={height} />;
  } else if (empty || !hasData) {
    body = (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography color="text.secondary">{emptyLabel}</Typography>
      </Box>
    );
  } else {
    body = (
      <Box sx={{ height, position: "relative" }}>
        <Line data={data} options={options} plugins={[outageBandsPlugin]} />
      </Box>
    );
  }

  return (
    <Card variant={outlined ? "outlined" : "elevation"}>
      <CardHeader
        title={title}
        slotProps={{ title: { variant: "subtitle1" } }}
        action={
          hint ? (
            <Tooltip title={hint}>
              <InfoOutlinedIcon
                fontSize="small"
                color="action"
                tabIndex={0}
                aria-label={hint}
                sx={{ cursor: "help", mt: 0.5 }}
              />
            </Tooltip>
          ) : undefined
        }
        sx={{ px: 2, pt: 2, pb: 0 }}
      />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>{body}</CardContent>
    </Card>
  );
};
